'use client';

import { Canvas } from '@react-three/fiber';
import { useRef, Suspense, useState, useEffect, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useDragControls, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Html, useGLTF } from '@react-three/drei';
import BackgroundModel from '../BackgroundModel/BackgroundModel';

interface KeyframeData {
  posX: number;
  posY: number;
  posZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  fov: number;
}

interface CameraControllerProps {
  keyframes: KeyframeData[];
  selectedKeyframe: number;
  disableScroll: boolean;
}

function InteractiveLight() {
  const lightRef = useRef<THREE.PointLight>(null);
  useFrame((state) => {
    if (lightRef.current) {
      const targetX = state.pointer.x * 6;
      const targetY = state.pointer.y * 6;
      lightRef.current.position.x += (targetX - lightRef.current.position.x) * 0.08;
      lightRef.current.position.y += (targetY - lightRef.current.position.y) * 0.08;
    }
  });
  return <pointLight ref={lightRef} intensity={18} color="#FFB6C1" distance={25} position={[0, 0, 8]} />;
}

// Dibujador de líneas seguro usando WebGL nativo básico sin shaders personalizados ni etiquetas JSX conflictivas (para evitar Type error y Shader Error)
function SafeTrajectoryLine({ points, color, opacity = 0.7 }: { points: [number, number, number][], color: string, opacity?: number }) {
  const line = useMemo(() => {
    const vertices = new Float32Array(points.flat());
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    const material = new THREE.LineBasicMaterial({
      color: new THREE.Color(color),
      transparent: true,
      opacity: opacity,
      linewidth: 1
    });
    return new THREE.Line(geometry, material);
  }, [points, color, opacity]);

  return <primitive object={line} />;
}

// Analiza la geometría del mesh/objeto 3D para calcular los centroides (centro exacto) del tubo
function calculateMeshCenterPoints(mesh: any, modelMatrix: THREE.Matrix4): THREE.Vector3[] {
  const posAttr = mesh.geometry.attributes.position;
  if (!posAttr) return [];

  const vertexCount = posAttr.count;
  const positions = posAttr.array;
  const numPoints = 20;

  let chosenCenters: THREE.Vector3[] = [];

  const uvAttr = mesh.geometry.attributes.uv;
  if (uvAttr) {
    const uvs = uvAttr.array;
    const centersU: THREE.Vector3[] = Array.from({ length: numPoints }, () => new THREE.Vector3());
    const countsU: number[] = Array.from({ length: numPoints }, () => 0);

    const centersV: THREE.Vector3[] = Array.from({ length: numPoints }, () => new THREE.Vector3());
    const countsV: number[] = Array.from({ length: numPoints }, () => 0);

    for (let i = 0; i < vertexCount; i++) {
      const vx = positions[i * 3];
      const vy = positions[i * 3 + 1];
      const vz = positions[i * 3 + 2];

      const u = uvs[i * 2];
      const v = uvs[i * 2 + 1];

      const uBucket = Math.min(Math.floor(u * numPoints), numPoints - 1);
      if (uBucket >= 0 && uBucket < numPoints) {
        centersU[uBucket].x += vx;
        centersU[uBucket].y += vy;
        centersU[uBucket].z += vz;
        countsU[uBucket]++;
      }

      const vBucket = Math.min(Math.floor(v * numPoints), numPoints - 1);
      if (vBucket >= 0 && vBucket < numPoints) {
        centersV[vBucket].x += vx;
        centersV[vBucket].y += vy;
        centersV[vBucket].z += vz;
        countsV[vBucket]++;
      }
    }

    for (let i = 0; i < numPoints; i++) {
      if (countsU[i] > 0) centersU[i].divideScalar(countsU[i]);
      if (countsV[i] > 0) centersV[i].divideScalar(countsV[i]);
    }

    for (let i = 0; i < numPoints; i++) {
      if (countsU[i] === 0) {
        let prevValid: THREE.Vector3 | null = null;
        let nextValid: THREE.Vector3 | null = null;
        for (let j = i - 1; j >= 0; j--) {
          if (countsU[j] > 0) { prevValid = centersU[j]; break; }
        }
        for (let j = i + 1; j < numPoints; j++) {
          if (countsU[j] > 0) { nextValid = centersU[j]; break; }
        }
        if (prevValid && nextValid) centersU[i].addVectors(prevValid, nextValid).multiplyScalar(0.5);
        else if (prevValid) centersU[i].copy(prevValid);
        else if (nextValid) centersU[i].copy(nextValid);
      }

      if (countsV[i] === 0) {
        let prevValid: THREE.Vector3 | null = null;
        let nextValid: THREE.Vector3 | null = null;
        for (let j = i - 1; j >= 0; j--) {
          if (countsV[j] > 0) { prevValid = centersV[j]; break; }
        }
        for (let j = i + 1; j < numPoints; j++) {
          if (countsV[j] > 0) { nextValid = centersV[j]; break; }
        }
        if (prevValid && nextValid) centersV[i].addVectors(prevValid, nextValid).multiplyScalar(0.5);
        else if (prevValid) centersV[i].copy(prevValid);
        else if (nextValid) centersV[i].copy(nextValid);
      }
    }

    let lengthU = 0;
    let lengthV = 0;
    for (let i = 0; i < numPoints - 1; i++) {
      lengthU += centersU[i].distanceTo(centersU[i + 1]);
      lengthV += centersV[i].distanceTo(centersV[i + 1]);
    }

    chosenCenters = lengthU > lengthV ? centersU : centersV;
  }

  // Si no hay UVs, usamos segmentación secuencial por índice del vertex buffer.
  // Es extremadamente robusto para tubos helicoidales/espirales ya que los vértices se escriben
  // de forma secuencial y lineal a lo largo de la curva del tubo al ser exportados.
  if (chosenCenters.length === 0) {
    const centersIndex: THREE.Vector3[] = Array.from({ length: numPoints }, () => new THREE.Vector3());
    const countsIndex: number[] = Array.from({ length: numPoints }, () => 0);

    for (let i = 0; i < vertexCount; i++) {
      const vx = positions[i * 3];
      const vy = positions[i * 3 + 1];
      const vz = positions[i * 3 + 2];

      const bucket = Math.min(Math.floor((i / vertexCount) * numPoints), numPoints - 1);
      centersIndex[bucket].x += vx;
      centersIndex[bucket].y += vy;
      centersIndex[bucket].z += vz;
      countsIndex[bucket]++;
    }

    for (let i = 0; i < numPoints; i++) {
      if (countsIndex[i] > 0) {
        centersIndex[i].divideScalar(countsIndex[i]);
      }
    }

    chosenCenters = centersIndex;
  }

  return chosenCenters.map(p => p.clone().applyMatrix4(modelMatrix));
}

// Componente helper que analiza y dibuja el camino exacto del centro del tubo en la escena
interface TubePathAnalyzerProps {
  onPointsCalculated: (points: THREE.Vector3[]) => void;
  onRawBoundsCalculated: (bounds: string) => void;
}

function TubePathAnalyzer({ onPointsCalculated, onRawBoundsCalculated }: TubePathAnalyzerProps) {
  const { scene } = useGLTF('/models/wadapi_3d_02.glb');
  const [points, setPoints] = useState<THREE.Vector3[]>([]);

  useEffect(() => {
    try {
      const modelMatrix = new THREE.Matrix4().compose(
        new THREE.Vector3(8.4, 6.4, 0.8),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.14, -1.99, 0.16)),
        new THREE.Vector3(4.1, 4.1, 4.1)
      );

      let tubeMesh: any = null;
      let maxVertices = 0;

      scene.traverse((child: any) => {
        if (child.geometry && child.geometry.attributes.position) {
          const posAttr = child.geometry.attributes.position;
          const count = posAttr ? posAttr.count : 0;
          if (count > maxVertices) {
            maxVertices = count;
            tubeMesh = child;
          }
        }
      });

      if (tubeMesh) {
        const posAttr = tubeMesh.geometry.attributes.position;
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        for (let i = 0; i < posAttr.count; i++) {
          const x = posAttr.getX(i);
          const y = posAttr.getY(i);
          const z = posAttr.getZ(i);
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
          if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
        }
        onRawBoundsCalculated(`Raw: X:[${minX.toFixed(1)}, ${maxX.toFixed(1)}] Y:[${minY.toFixed(1)}, ${maxY.toFixed(1)}] Z:[${minZ.toFixed(1)}, ${maxZ.toFixed(1)}]`);

        // Actualizar matrices mundiales del GLTF para que incorpore todas sus transformaciones de padres
        scene.updateMatrixWorld(true);
        const combinedMatrix = modelMatrix.clone().multiply(tubeMesh.matrixWorld);

        const calculated = calculateMeshCenterPoints(tubeMesh, combinedMatrix);
        if (calculated.length > 0) {
          setPoints(calculated);
          onPointsCalculated(calculated);
        }
      }
    } catch (err: any) {
      console.error('Error in TubePathAnalyzer:', err);
    }
  }, [scene, onPointsCalculated, onRawBoundsCalculated]);

  if (points.length === 0) return null;

  const formattedPoints = points.map(p => [p.x, p.y, p.z] as [number, number, number]);

  return (
    <group>
      <SafeTrajectoryLine points={formattedPoints} color="#22c55e" opacity={0.85} />
      {points.map((p, idx) => (
        <group key={idx} position={[p.x, p.y, p.z]}>
          <mesh>
            <sphereGeometry args={[0.07, 16, 16]} />
            <meshBasicMaterial color="#22c55e" toneMapped={false} />
          </mesh>
          <Html distanceFactor={10} center className="select-none pointer-events-none">
            <div className="px-[0.35rem] py-[0.1rem] rounded font-mono font-bold text-[8px] text-white bg-green-600 border border-green-400 shadow-md">
              {idx + 1}
            </div>
          </Html>
        </group>
      ))}
    </group>
  );
}

function interpolate(k0: KeyframeData, k1: KeyframeData, t: number): { pos: [number, number, number], rot: [number, number, number], fov: number } {
  const smoothT = t * t * (3 - 2 * t);
  
  const posX = k0.posX + (k1.posX - k0.posX) * smoothT;
  const posY = k0.posY + (k1.posY - k0.posY) * smoothT;
  const posZ = k0.posZ + (k1.posZ - k0.posZ) * smoothT;
  
  const rotX = k0.rotX + (k1.rotX - k0.rotX) * smoothT;
  const rotY = k0.rotY + (k1.rotY - k0.rotY) * smoothT;
  const rotZ = k0.rotZ + (k1.rotZ - k0.rotZ) * smoothT;
  
  const fov = k0.fov + (k1.fov - k0.fov) * smoothT;
  
  return {
    pos: [posX, posY, posZ],
    rot: [rotX, rotY, rotZ],
    fov
  };
}

function CameraController({ keyframes, selectedKeyframe, disableScroll }: CameraControllerProps) {
  const scrollProgress = useRef(0);
  const lerpProgress = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        scrollProgress.current = scrollTop / docHeight;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useFrame((state) => {
    let targetPos: [number, number, number];
    let targetRot: [number, number, number];
    let targetFov: number;

    if (disableScroll) {
      const k = keyframes[selectedKeyframe];
      targetPos = [k.posX, k.posY, k.posZ];
      targetRot = [k.rotX, k.rotY, k.rotZ];
      targetFov = k.fov;
    } else {
      lerpProgress.current += (scrollProgress.current - lerpProgress.current) * 0.05;
      
      const totalSegments = keyframes.length - 1;
      const scaledProgress = lerpProgress.current * totalSegments;
      const segment = Math.min(Math.floor(scaledProgress), totalSegments - 1);
      const t = scaledProgress - segment;
      
      const res = interpolate(keyframes[segment], keyframes[segment + 1], t);
      targetPos = res.pos;
      targetRot = res.rot;
      targetFov = res.fov;
    }

    const mouseParallaxX = state.pointer.x * 0.3;
    const mouseParallaxY = state.pointer.y * 0.3;

    state.camera.position.set(
      targetPos[0] + mouseParallaxX,
      targetPos[1] + mouseParallaxY,
      targetPos[2]
    );

    state.camera.rotation.set(targetRot[0], targetRot[1], targetRot[2]);

    if (state.camera instanceof THREE.PerspectiveCamera) {
      state.camera.fov = targetFov;
      state.camera.updateProjectionMatrix();
    }
  });

  return null;
}

export default function SceneCanvas() {
  const t = useTranslations('DebugModel');
  const dragControls = useDragControls();

  const [selectedKeyframe, setSelectedKeyframe] = useState(0);
  const [disableScroll, setDisableScroll] = useState(true);
  const [copied, setCopied] = useState(false);

  // 20 Puntos calculados del centro real del tubo
  const [computedPoints, setComputedPoints] = useState<THREE.Vector3[]>([]);
  const [rawBounds, setRawBounds] = useState<string>("Raw: loading...");

  // Keyframes de la cámara (ahora 20 puntos)
  const [keyframes, setKeyframes] = useState<KeyframeData[]>(
    Array.from({ length: 20 }, () => ({ posX: 0, posY: 0, posZ: 7.5, rotX: 0, rotY: 0, rotZ: 0, fov: 62 }))
  );

  const pathLinePoints = useMemo(() => {
    const sampled: [number, number, number][] = [];
    const samples = 100;
    const totalSegments = keyframes.length - 1;

    for (let i = 0; i <= samples; i++) {
      const progress = i / samples;
      const scaledProgress = progress * totalSegments;
      const segment = Math.min(Math.floor(scaledProgress), totalSegments - 1);
      const localT = scaledProgress - segment;
      
      const res = interpolate(keyframes[segment], keyframes[segment + 1], localT);
      sampled.push(res.pos);
    }
    return sampled;
  }, [keyframes]);

  const updateKeyframe = (field: keyof KeyframeData, value: number) => {
    setKeyframes((prev) => {
      const next = [...prev];
      next[selectedKeyframe] = {
        ...next[selectedKeyframe],
        [field]: value
      };
      return next;
    });
  };

  const copyFromPrev = () => {
    if (selectedKeyframe > 0) {
      setKeyframes((prev) => {
        const next = [...prev];
        next[selectedKeyframe] = { ...next[selectedKeyframe - 1] };
        return next;
      });
    }
  };

  const onPointsCalculated = useCallback((points: THREE.Vector3[]) => {
    setComputedPoints(points);
  }, []);

  const reverseComputedPoints = () => {
    setComputedPoints((prev) => [...prev].reverse());
  };

  // Copia el punto seleccionado del tubo directamente al Keyframe de cámara que esté ACTIVO
  const handleCopyPointToKeyframe = (idx: number) => {
    if (computedPoints.length === 0) return;
    const pos = computedPoints[idx];

    // Calcula dirección del lookAt hacia el siguiente punto en el orden del recorrido
    const nextPtIdx = Math.min(idx + 1, 19);
    const nextPos = computedPoints[nextPtIdx];
    
    const dir = new THREE.Vector3();
    if (nextPtIdx !== idx) {
      dir.subVectors(nextPos, pos).normalize();
    } else {
      const prevPos = computedPoints[Math.max(idx - 1, 0)];
      dir.subVectors(pos, prevPos).normalize();
    }

    const dummy = new THREE.Object3D();
    dummy.position.copy(pos);
    dummy.lookAt(pos.clone().add(dir));

    // Reemplaza los datos en el keyframe activo (selectedKeyframe)
    setKeyframes((prev) => {
      const next = [...prev];
      next[selectedKeyframe] = {
        ...next[selectedKeyframe],
        posX: parseFloat(pos.x.toFixed(2)),
        posY: parseFloat(pos.y.toFixed(2)),
        posZ: parseFloat(pos.z.toFixed(2)),
        rotX: parseFloat(dummy.rotation.x.toFixed(2)),
        rotY: parseFloat(dummy.rotation.y.toFixed(2)),
        rotZ: parseFloat(dummy.rotation.z.toFixed(2)),
      };
      return next;
    });

    // Copia al portapapeles en formato string
    navigator.clipboard.writeText(`[${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
    setDisableScroll(true);
  };

  const autofillCameraFromMesh = () => {
    if (computedPoints.length === 0) return;

    const dummy = new THREE.Object3D();
    
    // Mapeamos los Keyframes directamente en el orden actual de computedPoints.
    // Esto permite al usuario controlar el sentido del recorrido (por ejemplo, si
    // invierte el orden del tubo con el botón "Invertir Orden", la cámara lo reflejará).
    const newKeyframes = keyframes.map((k, idx) => {
      const ptIdx = idx;
      const pos = computedPoints[ptIdx];

      // Mirada de la cámara apunta al siguiente punto en el orden del recorrido (ptIdx + 1)
      const nextPtIdx = Math.min(ptIdx + 1, 19);
      const nextPos = computedPoints[nextPtIdx];

      const dir = new THREE.Vector3();
      if (nextPtIdx !== ptIdx) {
        dir.subVectors(nextPos, pos).normalize();
      } else {
        const prevPos = computedPoints[Math.max(ptIdx - 1, 0)];
        dir.subVectors(pos, prevPos).normalize();
      }

      dummy.position.copy(pos);
      dummy.lookAt(pos.clone().add(dir));

      return {
        posX: parseFloat(pos.x.toFixed(2)),
        posY: parseFloat(pos.y.toFixed(2)),
        posZ: parseFloat(pos.z.toFixed(2)),
        rotX: parseFloat(dummy.rotation.x.toFixed(2)),
        rotY: parseFloat(dummy.rotation.y.toFixed(2)),
        rotZ: parseFloat(dummy.rotation.z.toFixed(2)),
        fov: 62
      };
    });

    setKeyframes(newKeyframes);
  };

  const copyPathConfig = () => {
    const formatted = {
      keyframes: keyframes.map((k) => ({
        position: [parseFloat(k.posX.toFixed(2)), parseFloat(k.posY.toFixed(2)), parseFloat(k.posZ.toFixed(2))],
        rotation: [parseFloat(k.rotX.toFixed(2)), parseFloat(k.rotY.toFixed(2)), parseFloat(k.rotZ.toFixed(2))],
        fov: parseFloat(k.fov.toFixed(1))
      }))
    };
    navigator.clipboard.writeText(JSON.stringify(formatted, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const current = keyframes[selectedKeyframe];

  return (
    <>
      {/* 3D Canvas Backdrop */}
      <div className="fixed inset-0 -z-10 w-full h-full bg-zinc-950">
        <Canvas
          camera={{ position: [0, 0, 7.5], fov: 62 }}
          dpr={[1, 2]}
          gl={{
            antialias: true,
            alpha: false,
            powerPreference: "high-performance",
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 1.2
          }}
        >
          <color attach="background" args={['#09090b']} />
          
          <Suspense fallback={null}>
            <ambientLight intensity={0.3} />
            <directionalLight position={[8, 5, -12]} intensity={5.5} color="#FFF0F5" />
            <directionalLight position={[5, 4, 7]} intensity={3.5} color="#FFE4E1" />
            <directionalLight position={[-5, 4, 8]} intensity={3.2} color="#FFD1DC" />
            <directionalLight position={[0, 8, 2]} intensity={1.5} color="#FFE4B5" />
            <pointLight position={[0, -6, -5]} intensity={15} color="#D01760" distance={30} />
            <InteractiveLight />

            {/* Model (Frozen/Static relative to camera path adjustments) */}
            <BackgroundModel disableScroll={true} />

            {/* Analizador de malla para extraer el camino central real */}
            <TubePathAnalyzer onPointsCalculated={onPointsCalculated} onRawBoundsCalculated={setRawBounds} />

            {/* Path Camera Controller */}
            <CameraController
              keyframes={keyframes}
              selectedKeyframe={selectedKeyframe}
              disableScroll={disableScroll}
            />

            {/* Smooth 3D Trajectory Line using native line rendering (Safe from Shader Error) */}
            <SafeTrajectoryLine points={pathLinePoints} color="#ec4899" opacity={0.6} />

            {/* Visual markers for the camera path keyframes */}
            {keyframes.map((k, idx) => (
              <group key={idx} position={[k.posX, k.posY, k.posZ]}>
                <mesh>
                  <sphereGeometry args={[0.1, 16, 16]} />
                  <meshBasicMaterial 
                    color={selectedKeyframe === idx ? "#ec4899" : "#3b82f6"} 
                    toneMapped={false} 
                    transparent
                    opacity={0.8}
                  />
                </mesh>
                <Html distanceFactor={8} center className="select-none pointer-events-none">
                  <div className={`px-1.5 py-0.5 rounded font-mono font-bold text-[9px] text-white shadow-md ${selectedKeyframe === idx ? 'bg-pink-600 border border-pink-400' : 'bg-zinc-800 border border-zinc-700'}`}>
                    {idx + 1}
                  </div>
                </Html>
              </group>
            ))}

          </Suspense>
        </Canvas>
      </div>

      {/* Modern High-Performance Vignette Overlay (Replaces WebGL post-processing EffectComposer/Vignette) */}
      <div className="fixed inset-0 pointer-events-none z-[9998] bg-[radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.85)_100%)]" />

      {/* Floating Panel showing computed center points on the left */}
      {computedPoints.length > 0 && (
        <div className="fixed top-[5rem] left-[1.5rem] z-[9999] w-[18rem] md:w-[20rem] bg-zinc-950/95 border border-zinc-800 rounded-[1rem] p-[1.25rem] backdrop-blur-md max-h-[75vh] overflow-y-auto flex flex-col gap-[0.75rem] text-[0.75rem] text-zinc-100 select-none font-sans shadow-2xl">
          <div className="flex flex-col border-b border-zinc-900 pb-[0.5rem] gap-1">
            <div className="flex items-center justify-between">
              <span className="font-bold tracking-tight text-zinc-200">Centro del Tubo (20 Puntos)</span>
              <button
                type="button"
                onClick={reverseComputedPoints}
                className="px-[0.5rem] py-[0.25rem] text-[0.65rem] bg-zinc-900 hover:bg-zinc-800 text-pink-400 font-bold border border-zinc-800 rounded-[0.25rem] transition-colors"
              >
                Invertir Orden
              </button>
            </div>
            <div className="text-[10px] text-zinc-400 font-mono select-all">
              {rawBounds}
            </div>
          </div>

          <button
            type="button"
            onClick={autofillCameraFromMesh}
            className="w-full py-[0.5rem] bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold rounded-[0.5rem] shadow-md transition-all duration-200"
          >
            Auto-Alinear Cámara a Puntos
          </button>

          <div className="flex flex-col gap-[0.375rem] max-h-[45vh] overflow-y-auto pr-[0.25rem]">
            {computedPoints.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800/40 rounded-[0.375rem] p-[0.375rem] font-mono text-[0.7rem]">
                <div className="flex items-center gap-[0.375rem]">
                  <span className="w-[1.25rem] h-[1.25rem] rounded-full bg-green-500/20 text-green-400 font-bold flex items-center justify-center text-[0.65rem]">
                    {idx + 1}
                  </span>
                  <span>
                    [{p.x.toFixed(2)}, {p.y.toFixed(2)}, {p.z.toFixed(2)}]
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyPointToKeyframe(idx)}
                  className="px-[0.375rem] py-[0.125rem] text-[0.6rm] bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded font-semibold transition-colors"
                >
                  Copiar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating draggable Camera Path Editor Panel */}
      <motion.div
        drag
        dragControls={dragControls}
        dragListener={false}
        dragMomentum={false}
        className="fixed bottom-[1.5rem] right-[1.5rem] z-[9999] w-[18rem] md:w-[22rem] bg-zinc-950/95 border border-zinc-800 rounded-[1rem] p-[1.25rem] backdrop-blur-md shadow-2xl flex flex-col gap-[0.75rem] text-[0.8rem] text-zinc-100 select-none font-sans"
      >
        {/* Header (Drag Handle) */}
        <div 
          onPointerDown={(e) => dragControls.start(e)}
          className="flex items-center justify-between border-b border-zinc-900 pb-[0.5rem] mb-[0.25rem] cursor-grab active:cursor-grabbing select-none"
        >
          <div className="flex items-center gap-[0.5rem]">
            <span className="w-[0.5rem] h-[0.5rem] rounded-full bg-pink-500 animate-pulse" />
            <span className="font-bold tracking-tight text-zinc-200">{t('titleCam')}</span>
          </div>
          <label className="flex items-center gap-[0.375rem] cursor-pointer text-zinc-400 hover:text-zinc-200" onPointerDown={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={disableScroll}
              onChange={(e) => setDisableScroll(e.target.checked)}
              className="accent-pink-500 rounded border-zinc-800 bg-zinc-900"
            />
            <span>{t('disableScroll')}</span>
          </label>
        </div>

        {/* 20 Keyframe Selector Tabs in a beautiful 10-column synthesizer grid */}
        <div className="grid grid-cols-10 gap-[0.125rem] p-[0.125rem] bg-zinc-900/60 rounded-[0.5rem] border border-zinc-800/40" onPointerDown={(e) => e.stopPropagation()}>
          {Array.from({ length: 20 }).map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => { setSelectedKeyframe(idx); setDisableScroll(true); }}
              className={`py-[0.375rem] text-center text-[0.7rem] rounded-[0.25rem] font-semibold transition-all duration-150 ${selectedKeyframe === idx ? 'bg-pink-500 text-white shadow-md' : 'text-zinc-400 hover:text-zinc-200'}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between px-[0.125rem]">
          <span className="text-[0.725rem] text-zinc-500 italic">
            {t('keyframeLabel')}: <strong className="text-pink-400 font-semibold">{selectedKeyframe + 1}</strong>
          </span>
          {selectedKeyframe > 0 && (
            <button
              type="button"
              onClick={copyFromPrev}
              onPointerDown={(e) => e.stopPropagation()}
              className="px-[0.5rem] py-[0.25rem] text-[0.675rem] bg-zinc-900 hover:bg-zinc-800 text-zinc-300 font-bold border border-zinc-800 rounded-[0.375rem] transition-colors"
            >
              {t('btnCopyPrev')}
            </button>
          )}
        </div>

        {/* Camera Position & Rotation Sliders */}
        <div className="flex flex-col gap-[0.5rem]" onPointerDown={(e) => e.stopPropagation()}>
          
          {/* Position X */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camPosX')}</span>
              <span className="font-mono text-pink-400">{current.posX.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="-20"
              max="20"
              step="0.1"
              value={current.posX}
              onChange={(e) => updateKeyframe('posX', parseFloat(e.target.value))}
              className="w-full accent-pink-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          {/* Position Y */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camPosY')}</span>
              <span className="font-mono text-pink-400">{current.posY.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="-20"
              max="20"
              step="0.1"
              value={current.posY}
              onChange={(e) => updateKeyframe('posY', parseFloat(e.target.value))}
              className="w-full accent-pink-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          {/* Position Z */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camPosZ')}</span>
              <span className="font-mono text-pink-400">{current.posZ.toFixed(1)}</span>
            </div>
            <input
              type="range"
              min="-30"
              max="25"
              step="0.1"
              value={current.posZ}
              onChange={(e) => updateKeyframe('posZ', parseFloat(e.target.value))}
              className="w-full accent-pink-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          <div className="w-full h-[1px] bg-zinc-900 my-[0.125rem]" />

          {/* Rotation X (Pitch) */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camRotX')}</span>
              <span className="font-mono text-blue-400">{current.rotX.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="-3.14"
              max="3.14"
              step="0.01"
              value={current.rotX}
              onChange={(e) => updateKeyframe('rotX', parseFloat(e.target.value))}
              className="w-full accent-blue-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          {/* Rotation Y (Yaw) */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camRotY')}</span>
              <span className="font-mono text-blue-400">{current.rotY.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="-3.14"
              max="3.14"
              step="0.01"
              value={current.rotY}
              onChange={(e) => updateKeyframe('rotY', parseFloat(e.target.value))}
              className="w-full accent-blue-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          {/* Rotation Z (Roll) */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camRotZ')}</span>
              <span className="font-mono text-blue-400">{current.rotZ.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="-3.14"
              max="3.14"
              step="0.01"
              value={current.rotZ}
              onChange={(e) => updateKeyframe('rotZ', parseFloat(e.target.value))}
              className="w-full accent-blue-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

          <div className="w-full h-[1px] bg-zinc-900 my-[0.125rem]" />

          {/* Camera FOV */}
          <div className="flex flex-col gap-[0.125rem]">
            <div className="flex justify-between text-zinc-400">
              <span>{t('camFov')}</span>
              <span className="font-mono text-emerald-400">{current.fov.toFixed(0)}°</span>
            </div>
            <input
              type="range"
              min="20"
              max="140"
              step="1"
              value={current.fov}
              onChange={(e) => updateKeyframe('fov', parseInt(e.target.value))}
              className="w-full accent-emerald-500 cursor-pointer h-1 bg-zinc-800 rounded-lg appearance-none"
            />
          </div>

        </div>

        {/* Action Button */}
        <button
          type="button"
          onClick={copyPathConfig}
          onPointerDown={(e) => e.stopPropagation()}
          className="w-full h-[2.5rem] mt-[0.25rem] bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-semibold rounded-[0.5rem] flex items-center justify-center gap-[0.5rem] shadow-[0_0_15px_rgba(255,0,127,0.2)] transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-pink-500 active:scale-98"
        >
          <span>{copied ? t('btnPathCopied') : t('btnCopyPath')}</span>
        </button>
      </motion.div>
    </>
  );
}
